import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  AutoAwesome as AutoAwesomeIcon,
  ExpandMore as ExpandMoreIcon,
  Image as ImageIcon,
  Refresh as RefreshIcon,
  Slideshow as SlideshowIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { useAuth } from "../hooks/useAuth";
import AppService from "../services/AppService";
import SlideshowService from "../services/SlideshowService";
import type { App } from "../types/app";
import type {
  CreateSlideshowPlanRequest,
  SlideshowJob,
  SlideshowReviewPayload,
} from "../types/slideshow";

const OBJECTIVES = [
  "App downloads",
  "Gain followers",
  "Get likes for the post",
  "Share the post",
  "Drive profile visits",
];
const VIRAL_PLAYBOOKS = [
  "5 things about",
  "What you should know",
  "Stop, you have to know this",
  "Before you scroll",
  "Nobody tells you this",
  "I wish I knew this earlier",
];
const UPSELL_PLAYBOOKS = [
  "Soft emotional CTA",
  "Proof-driven CTA",
  "Urgency CTA",
  "Curiosity CTA",
  "Save & share CTA",
];

function statusColor(status: SlideshowJob["status"]) {
  if (status === "completed") return "success";
  if (status === "failed") return "error";
  if (status === "processing") return "info";
  if (status === "review_pending") return "secondary";
  return "warning";
}

function reviewFromJob(job: SlideshowJob): SlideshowReviewPayload {
  return {
    conceptSummary: String(job.planning?.conceptSummary || ""),
    qualityConstraints: Array.isArray(job.planning?.qualityConstraints)
      ? [...job.planning!.qualityConstraints]
      : [],
    slides: Array.isArray(job.planning?.slides) ? [...job.planning!.slides] : [],
    captionsByPlatform:
      job.planning?.captionsByPlatform && typeof job.planning.captionsByPlatform === "object"
        ? { ...job.planning.captionsByPlatform }
        : {},
  };
}

function groupKey(iso: string) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function SlideshowMaker() {
  const { user } = useAuth();
  const [apps, setApps] = useState<App[]>([]);
  const [jobs, setJobs] = useState<SlideshowJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [startingJobId, setStartingJobId] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [reviewDrafts, setReviewDrafts] = useState<
    Record<string, SlideshowReviewPayload>
  >({});

  const [form, setForm] = useState<CreateSlideshowPlanRequest>({
    appId: "",
    objective: OBJECTIVES[0],
    viralPlaybook: VIRAL_PLAYBOOKS[0],
    upsellPlaybook: UPSELL_PLAYBOOKS[0],
    slideCount: 5,
    creativeDirection: "",
  });

  const planJobs = useMemo(() => [...jobs], [jobs]);
  const groupedPlans = useMemo(() => {
    const grouped = new Map<string, SlideshowJob[]>();
    planJobs.forEach((job) => {
      const key = groupKey(job.createdAt);
      const existing = grouped.get(key);
      if (existing) existing.push(job);
      else grouped.set(key, [job]);
    });
    return Array.from(grouped.entries());
  }, [planJobs]);

  const loadApps = useCallback(async () => {
    if (!user?.email) return;
    const data = await AppService.getUserApps(user.email);
    setApps(data);
    if (!form.appId && data[0]?.appId) {
      setForm((prev) => ({ ...prev, appId: data[0].appId }));
    }
  }, [form.appId, user?.email]);

  const loadJobs = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await SlideshowService.listJobs(
        form.appId ? { appId: form.appId } : undefined,
      );
      setJobs(data);
      setReviewDrafts((prev) => {
        const next = { ...prev };
        data.forEach((job) => {
          if (job.status === "review_pending" && !next[job.jobId]) {
            next[job.jobId] = reviewFromJob(job);
          }
        });
        return next;
      });
    } catch (loadError: any) {
      setError(loadError?.message || "Failed to load slideshow jobs.");
    } finally {
      setLoading(false);
    }
  }, [form.appId, user]);

  useEffect(() => {
    loadApps();
  }, [loadApps]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const handleCreatePlan = async () => {
    setError(null);
    setCreating(true);
    try {
      await SlideshowService.createPlan(form);
      setActiveTab(1);
      await loadJobs();
    } catch (createError: any) {
      setError(createError?.message || "Failed to create slideshow plan.");
    } finally {
      setCreating(false);
    }
  };

  const handleStart = async (job: SlideshowJob) => {
    const review = reviewDrafts[job.jobId];
    if (!review) return;
    setStartingJobId(job.jobId);
    try {
      await SlideshowService.startGeneration({ jobId: job.jobId, review });
      setActiveTab(2);
      await loadJobs();
    } catch (startError: any) {
      setError(startError?.message || "Failed to start generation.");
    } finally {
      setStartingJobId("");
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2.5 }}>
      <Card sx={{ mb: 2, borderRadius: 2.5 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab icon={<AutoAwesomeIcon />} iconPosition="start" label="Create Plan" />
          <Tab icon={<SlideshowIcon />} iconPosition="start" label="Slide Plans" />
          <Tab icon={<ImageIcon />} iconPosition="start" label="Slide Jobs" />
        </Tabs>
      </Card>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <FormControl fullWidth size="small">
            <InputLabel>Registered App</InputLabel>
            <Select
              label="Registered App"
              value={form.appId}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, appId: event.target.value }))
              }
            >
              {apps.map((app) => (
                <MenuItem key={app.appId} value={app.appId}>
                  {app.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {activeTab === 0 && (
        <Card variant="outlined">
          <CardContent sx={{ display: "grid", gap: 1.5 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Objective</InputLabel>
                  <Select
                    label="Objective"
                    value={form.objective}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, objective: event.target.value }))
                    }
                  >
                    {OBJECTIVES.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Viral Playbook</InputLabel>
                  <Select
                    label="Viral Playbook"
                    value={form.viralPlaybook}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, viralPlaybook: event.target.value }))
                    }
                  >
                    {VIRAL_PLAYBOOKS.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Upsell CTA Playbook</InputLabel>
                  <Select
                    label="Upsell CTA Playbook"
                    value={form.upsellPlaybook}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, upsellPlaybook: event.target.value }))
                    }
                  >
                    {UPSELL_PLAYBOOKS.map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Slide Count</InputLabel>
                  <Select
                    label="Slide Count"
                    value={String(form.slideCount)}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        slideCount: Number(event.target.value),
                      }))
                    }
                  >
                    {[3, 4, 5, 6, 7, 8, 9, 10].map((count) => (
                      <MenuItem key={count} value={String(count)}>
                        {count} slides
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <TextField
              size="small"
              label="Creative Direction (Optional)"
              multiline
              minRows={3}
              value={form.creativeDirection || ""}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, creativeDirection: event.target.value }))
              }
            />
            <Button
              variant="contained"
              startIcon={<AutoAwesomeIcon />}
              disabled={creating || !form.appId}
              onClick={handleCreatePlan}
            >
              {creating ? "Creating..." : "Create Slideshow Plan"}
            </Button>
          </CardContent>
        </Card>
      )}

      {activeTab === 1 && (
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
              <Typography variant="h6">Slide Plans</Typography>
              <Button size="small" startIcon={<RefreshIcon />} onClick={loadJobs}>
                Refresh
              </Button>
            </Box>
            {loading ? (
              <Typography color="text.secondary">Loading...</Typography>
            ) : groupedPlans.length === 0 ? (
              <Typography color="text.secondary">No plans yet.</Typography>
            ) : (
              groupedPlans.map(([dateLabel, dateJobs], idx) => (
                <Accordion key={dateLabel} defaultExpanded={idx === 0}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                      <Typography sx={{ fontWeight: 700 }}>{dateLabel}</Typography>
                      <Chip size="small" label={`${dateJobs.length} plans`} />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: "grid", gap: 1 }}>
                      {dateJobs.map((job) => {
                        const review = reviewDrafts[job.jobId];
                        return (
                          <Paper key={job.jobId} variant="outlined" sx={{ p: 1.2 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                              <Typography sx={{ fontWeight: 700 }}>
                                {job.inputs.viralPlaybook} • {job.inputs.slideCount} slides
                              </Typography>
                              <Chip size="small" color={statusColor(job.status) as any} label={job.status} />
                            </Box>
                            {job.status === "review_pending" && review ? (
                              <Box sx={{ display: "grid", gap: 1 }}>
                                {review.slides.map((slide, index) => (
                                  <TextField
                                    key={`${job.jobId}_${slide.index}`}
                                    size="small"
                                    multiline
                                    minRows={2}
                                    label={`Slide ${slide.index} Prompt (${slide.role})`}
                                    value={slide.imagePrompt}
                                    onChange={(event) =>
                                      setReviewDrafts((prev) => {
                                        const next = [...review.slides];
                                        next[index] = {
                                          ...next[index],
                                          imagePrompt: event.target.value,
                                        };
                                        return {
                                          ...prev,
                                          [job.jobId]: { ...review, slides: next },
                                        };
                                      })
                                    }
                                  />
                                ))}
                                <Button
                                  variant="contained"
                                  disabled={startingJobId === job.jobId}
                                  onClick={() => handleStart(job)}
                                >
                                  {startingJobId === job.jobId
                                    ? "Starting..."
                                    : "Start Generation"}
                                </Button>
                              </Box>
                            ) : (
                              <Typography color="text.secondary" variant="body2">
                                This plan is already in generation/completed state.
                              </Typography>
                            )}
                          </Paper>
                        );
                      })}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 2 && (
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
              <Typography variant="h6">Slide Jobs</Typography>
              <Button size="small" startIcon={<RefreshIcon />} onClick={loadJobs}>
                Refresh
              </Button>
            </Box>
            {loading ? (
              <Typography color="text.secondary">Loading...</Typography>
            ) : jobs.length === 0 ? (
              <Typography color="text.secondary">No jobs yet.</Typography>
            ) : (
              <Box sx={{ display: "grid", gap: 1.5 }}>
                {jobs.map((job) => (
                  <Paper key={job.jobId} variant="outlined" sx={{ p: 1.2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {job.inputs.objective} • {job.inputs.slideCount} slides
                      </Typography>
                      <Chip size="small" color={statusColor(job.status) as any} label={job.status} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {job.progress.message}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.max(0, Math.min(100, job.progress.percent))}
                      sx={{ mt: 1, mb: 1.2 }}
                    />
                    {Array.isArray(job.slideAssets) && job.slideAssets.length > 0 && (
                      <Grid container spacing={1}>
                        {job.slideAssets.map((slide) => (
                          <Grid item xs={6} md={3} key={`${job.jobId}_${slide.slideIndex}`}>
                            <Box
                              component="img"
                              src={slide.asset?.downloadUrl}
                              alt={`Slide ${slide.slideIndex}`}
                              onClick={() => setPreviewImage(slide.asset?.downloadUrl || null)}
                              sx={{
                                width: "100%",
                                aspectRatio: "9 / 16",
                                objectFit: "cover",
                                borderRadius: 1,
                                border: "1px solid",
                                borderColor: "divider",
                                cursor: "zoom-in",
                              }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    )}
                    {job.zipAsset?.downloadUrl && (
                      <Box sx={{ mt: 1.25 }}>
                        <Button
                          size="small"
                          startIcon={<DownloadIcon />}
                          variant="outlined"
                          onClick={() => window.open(job.zipAsset!.downloadUrl, "_blank")}
                        >
                          Download Slides ZIP
                        </Button>
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog
        open={Boolean(previewImage)}
        onClose={() => setPreviewImage(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Slide Preview</DialogTitle>
        <DialogContent>
          {previewImage && (
            <Box component="img" src={previewImage} alt="Slide preview" sx={{ width: "100%" }} />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
}
